<template>
    <div
        class="upload-speed inline-flex max-w-full items-center gap-3 rounded-full border border-primary/15 bg-background/85 px-3 py-1.5 text-xs text-text shadow-sm backdrop-blur"
        aria-live="polite"
    >
        <template v-if="stats.serverSeconds === null">
            <svg
                class="h-5 w-14 shrink-0 text-primary"
                viewBox="0 0 56 20"
                preserveAspectRatio="none"
                aria-hidden="true"
            >
                <path v-if="sparkline" :d="sparkline.area" class="fill-current opacity-15"/>
                <path
                    v-if="sparkline"
                    :d="sparkline.line"
                    class="fill-none stroke-current"
                    stroke-width="1.5"
                    stroke-linejoin="round"
                    stroke-linecap="round"
                    vector-effect="non-scaling-stroke"
                />
            </svg>
            <span class="font-semibold tabular-nums text-primary">
                {{ stats.loaded ? `${ formatUploadBytes(stats.bytesPerSecond) }/s` : t('starting') }}
            </span>
            <span class="hidden h-3 w-px bg-text-light/30 sm:block" aria-hidden="true"></span>
            <span v-if="stats.total" class="hidden truncate tabular-nums text-text-light sm:inline">
                {{ formatUploadBytes(stats.loaded) }} / {{ formatUploadBytes(stats.total) }}
            </span>
            <template v-if="stats.secondsRemaining !== null">
                <span class="h-3 w-px bg-text-light/30" aria-hidden="true"></span>
                <span class="tabular-nums text-text-light">
                    {{ t('remaining', { time: formatUploadDuration(stats.secondsRemaining) }) }}
                </span>
            </template>
        </template>
        <template v-else>
            <span class="flex items-center gap-1" aria-hidden="true">
                <span class="upload-speed-dot"></span>
                <span class="upload-speed-dot"></span>
                <span class="upload-speed-dot"></span>
            </span>
            <span class="font-semibold text-primary">{{ t('saving') }}</span>
            <span class="h-3 w-px bg-text-light/30" aria-hidden="true"></span>
            <span class="tabular-nums text-text-light">
                {{ t('average', { speed: `${ formatUploadBytes(stats.averageBytesPerSecond) }/s` }) }}
            </span>
            <span class="tabular-nums text-text-light">{{ formatUploadDuration(stats.serverSeconds) }}</span>
        </template>
    </div>
</template>

<script setup lang="ts">
import type { UploadSpeedStats } from '~/composables/useUploadSpeed'

const props = defineProps<{ stats: UploadSpeedStats }>()
const { t } = useI18n()

const WIDTH = 56
const HEIGHT = 20

const sparkline = computed(() => {
    const history = props.stats.history
    if (history.length < 2) return null
    const peak = Math.max(...history, 1)
    const step = WIDTH / (history.length - 1)
    const points = history.map((value, index) =>
        `${ (index * step).toFixed(1) },${ (HEIGHT - 1 - (value / peak) * (HEIGHT - 2)).toFixed(1) }`)
    const line = `M${ points.join(' L') }`
    return { line, area: `${ line } L${ WIDTH },${ HEIGHT } L0,${ HEIGHT } Z` }
})
</script>

<style scoped lang="scss">
.upload-speed-dot {
    width: 0.375rem;
    height: 0.375rem;
    border-radius: 9999px;
    background-color: var(--color-primary);
    animation: upload-speed-pulse 1s ease-in-out infinite;

    &:nth-child(2) {
        animation-delay: 150ms;
    }

    &:nth-child(3) {
        animation-delay: 300ms;
    }
}

@keyframes upload-speed-pulse {
    0%, 80%, 100% {
        opacity: 0.3;
        transform: translateY(0);
    }
    40% {
        opacity: 1;
        transform: translateY(-2px);
    }
}

@media (prefers-reduced-motion: reduce) {
    .upload-speed-dot {
        animation: none;
        opacity: 0.7;
    }
}
</style>

<i18n lang="yaml">
en:
    starting: Starting…
    remaining: '{time} left'
    saving: Saving on server
    average: 'avg {speed}'
nl:
    starting: Starten…
    remaining: 'nog {time}'
    saving: Opslaan op server
    average: 'gem. {speed}'
</i18n>
